import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import { CURRENCIES } from "../../config/currencies.config";
import { saveStoreSettings, useStoreSettings,uploadStoreImage , deleteStoreImage} from "../../controllers/settings.controller";
import {toast} from "react-hot-toast"
import { mutate } from "swr";
import Popover from "../../components/Popover";
import { IconExternalLink, IconQrcode, IconTrash, IconUpload, IconMapPin, IconRefresh, IconCloud, IconDatabase, IconCoin } from "@tabler/icons-react";
import { iconStroke, getSyncMode, setSyncMode } from "../../config/config";
import QRCode from "qrcode";
import { getQRMenuLink } from "../../helpers/QRMenuHelper";
import imageCompression from "browser-image-compression";
import { getImageURL } from "../../helpers/ImageHelper";
import { useTheme } from "../../contexts/ThemeContext";
import { getUserDetailsInLocalStorage } from "../../helpers/UserDetails";

export default function SettingDetailsPage() {
  const { t } = useTranslation();
  const storeNameRef = useRef();
  const addressRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const currencyRef = useRef();
  const isQRMenuEnabledRef = useRef();
  const isQROrderEnabledRef = useRef();
  const isQRViewBillEnabledRef = useRef();
  const isFeedbackEnabledRef = useRef();
  const panNoRef = useRef();
  const cbmsPasswordRef = useRef();
  const isVatRegisteredRef = useRef();
  
  // Marketplace & Discovery Refs
  const businessTypeRef = useRef();
  const descriptionRef = useRef();
  const websiteRef = useRef();
  const latitudeRef = useRef();
  const longitudeRef = useRef();
  const landmarkRef = useRef();
  const cityRef = useRef();
  const stateRef = useRef();
  const countryRef = useRef();
  const postalCodeRef = useRef();
  const foodOrderingEnabledRef = useRef();
  const roomBookingEnabledRef = useRef();
  const deliveryEnabledRef = useRef();
  const pickupEnabledRef = useRef();
  const trekkingEnabledRef = useRef();
  const deliveryRadiusKmRef = useRef();
  const minOrderAmountRef = useRef();
  const deliveryChargeRef = useRef();
  const freeDeliveryThresholdRef = useRef();

  const { theme } = useTheme();

  const { APIURL, data, error, isLoading } = useStoreSettings();

  const [syncMode, setSyncModeState] = useState(() => {
    return getSyncMode() || 'online_only';
  });

  const [stockSellingPolicy, setStockSellingPolicy] = useState(() => {
    return data?.stockSellingPolicy || data?.stock_selling_policy || 'allow_negative';
  });

  const [isTipEnabled, setIsTipEnabled] = useState(() => {
    return Boolean(data?.is_tip_enabled || data?.isTipEnabled);
  });
  const [tipDefaultType, setTipDefaultType] = useState(() => {
    return data?.tip_default_type || data?.tipDefaultType || 'direct';
  });
  const [tipDefaultPercentages, setTipDefaultPercentages] = useState(() => {
    return data?.tip_default_percentages || data?.tipDefaultPercentages || '5,10,15';
  });

  useEffect(() => {
    if (data?.syncMode || data?.sync_mode) {
      const mode = data.syncMode || data.sync_mode;
      setSyncModeState(mode);
      setSyncMode(mode);
    }
    if (data?.stockSellingPolicy || data?.stock_selling_policy) {
      setStockSellingPolicy(data.stockSellingPolicy || data.stock_selling_policy);
    }
    if (data) {
      if (data.is_tip_enabled !== undefined || data.isTipEnabled !== undefined) {
        setIsTipEnabled(Boolean(data.is_tip_enabled || data.isTipEnabled));
      }
      if (data.tip_default_type || data.tipDefaultType) {
        setTipDefaultType(data.tip_default_type || data.tipDefaultType);
      }
      if (data.tip_default_percentages || data.tipDefaultPercentages) {
        setTipDefaultPercentages(data.tip_default_percentages || data.tipDefaultPercentages);
      }
    }
  }, [data]);

  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname.includes('workflow') || 
      location.pathname.includes('store-details') || 
      location.pathname.includes('sync') ||
      location.hash === '#workflow'
    ) {
      const el = document.getElementById('workflow-section');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      }
    }
  }, [location, isLoading, data]);

  if(isLoading || !data) {
    return <Page className="px-8 py-6">{t('settings.please_wait')}</Page>
  }

  if(error) {
    console.error(error);
    return <Page className="px-8 py-6">{t('settings.error_loading_data')}</Page>;
  }

  const { 
    storeImage = null, 
    storeName = "", 
    email = "", 
    address = "", 
    phone = "", 
    currency = "", 
    isQRMenuEnabled = false, 
    uniqueQRCode = "", 
    isQROrderEnabled = false, 
    isQRViewBillEnabled = false,
    isFeedbackEnabled = false, 
    uniqueId = "", 
    panNo = "", 
    cbmsPassword = "", 
    isVatRegistered = false, 
    businessType = "", 
    description = "", 
    website = "", 
    socialLinks = {}, 
    latitude = "", 
    longitude = "", 
    landmark = "", 
    city = "", 
    state = "", 
    country = "", 
    postalCode = "", 
    foodOrderingEnabled = false, 
    roomBookingEnabled = false, 
    deliveryEnabled = false, 
    pickupEnabled = false, 
    trekkingEnabled = false, 
    deliveryRadiusKm = 5, 
    minOrderAmount = 0, 
    deliveryCharge = 0, 
    freeDeliveryThreshold = 0, 
    operatingHours = null 
  } = data || {};

  const QR_MENU_LINK = getQRMenuLink(uniqueQRCode);

  const btnSave = async () => {
    const storeName = storeNameRef.current.value;
    const address = addressRef.current.value;
    const email = emailRef.current.value;
    const phone = phoneRef.current.value;
    const currency = currencyRef.current.value;
    const isQRMenuEnabled = isQRMenuEnabledRef.current.checked;
    const isQROrderEnabled = isQROrderEnabledRef.current.checked;
    const isQRViewBillEnabled = isQRViewBillEnabledRef.current?.checked || false;
    const isFeedbackEnabled = isFeedbackEnabledRef.current.checked;
    const panNo = panNoRef.current.value;
    const cbmsPassword = cbmsPasswordRef.current.value;
    const isVatRegistered = isVatRegisteredRef.current.checked;

    const businessType = businessTypeRef.current.value;
    const description = descriptionRef.current.value;
    const website = websiteRef.current.value;
    const latitude = latitudeRef.current.value;
    const longitude = longitudeRef.current.value;
    const landmark = landmarkRef.current.value;
    const city = cityRef.current.value;
    const state = stateRef.current.value;
    const country = countryRef.current.value;
    const postalCode = postalCodeRef.current.value;
    const foodOrderingEnabled = foodOrderingEnabledRef.current?.checked || false;
    const roomBookingEnabled = roomBookingEnabledRef.current?.checked || false;
    const deliveryEnabled = deliveryEnabledRef.current?.checked || false;
    const pickupEnabled = pickupEnabledRef.current?.checked || false;
    const trekkingEnabled = trekkingEnabledRef.current?.checked || false;
    const deliveryRadiusKm = deliveryRadiusKmRef.current.value || 5;
    const minOrderAmount = minOrderAmountRef.current.value || 0;
    const deliveryCharge = deliveryChargeRef.current.value || 0;
    const freeDeliveryThreshold = freeDeliveryThresholdRef.current.value || 0;

    try {

      toast.loading(t('settings.please_wait'));
      const res = await saveStoreSettings(storeName, address, phone, email, currency, null, isQRMenuEnabled , isQROrderEnabled, isFeedbackEnabled, panNo, cbmsPassword, isVatRegistered, businessType, description, website, null, latitude, longitude, landmark, city, state, country, postalCode, foodOrderingEnabled, roomBookingEnabled, deliveryEnabled, pickupEnabled, trekkingEnabled, deliveryRadiusKm, minOrderAmount, deliveryCharge, freeDeliveryThreshold, null, syncMode, stockSellingPolicy, isQRViewBillEnabled, isTipEnabled, tipDefaultType, tipDefaultPercentages);

      if(res.status == 200) {
        const previousMode = getSyncMode();
        setSyncMode(syncMode);
        if (window.electron?.ipcRenderer?.send) {
          window.electron.ipcRenderer.send('set-sync-mode', syncMode);
        } else if (typeof window.require === 'function') {
          try {
            const { ipcRenderer } = window.require('electron');
            if (ipcRenderer) {
              ipcRenderer.send('set-sync-mode', syncMode);
            }
          } catch (e) {}
        }
        await mutate(APIURL);
        toast.dismiss();

        if (previousMode !== syncMode) {
          if (syncMode === 'offline_sync') {
            toast.loading("Syncing all catalog and settings to local offline database... Please wait.");
            try {
              if (window.electron?.ipcRenderer?.invoke) {
                await window.electron.ipcRenderer.invoke('trigger-full-sync');
              } else if (typeof window.require === 'function') {
                const { ipcRenderer } = window.require('electron');
                if (ipcRenderer?.invoke) {
                  await ipcRenderer.invoke('trigger-full-sync');
                }
              }
            } catch (syncErr) {
              console.warn("Offline sync note:", syncErr);
            }
          }
          toast.dismiss();
          toast.success("Workflow mode updated. Reloading app to apply changes...");
          setTimeout(() => {
            window.location.reload();
          }, 800);
          return;
        }

        toast.success(res.data.message || "Settings saved successfully!");
      }

    } catch (error) {
      const message = error?.response?.data?.message || t('settings.something_went_wrong');
      console.error(error);

      toast.dismiss();
      toast.error(message);
    }
  };

  const btnDownloadMenuQR = async () => {
    try {
      const qrDataURL = await QRCode.toDataURL(QR_MENU_LINK, {width: 1080});
      const link = document.createElement("a");
      link.download="qr.png";
      link.href=qrDataURL;
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
    }
  }

  const handleGetLocation = () => {
    if ("geolocation" in navigator) {
      toast.loading("Fetching location...");
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          
          if(latitudeRef.current) latitudeRef.current.value = lat;
          if(longitudeRef.current) longitudeRef.current.value = lon;

          try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
            const data = await response.json();
            
            if (data && data.address) {
              if(cityRef.current) cityRef.current.value = data.address.city || data.address.town || data.address.village || "";
              if(stateRef.current) stateRef.current.value = data.address.state || "";
              if(countryRef.current) countryRef.current.value = data.address.country || "";
              if(postalCodeRef.current) postalCodeRef.current.value = data.address.postcode || "";
            }
            toast.dismiss();
            toast.success("Location fetched successfully");
          } catch (error) {
            console.error("Error fetching location details:", error);
            toast.dismiss();
            toast.success("Coordinates fetched, but failed to get address details");
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.dismiss();
          toast.error("Failed to get location: " + error.message);
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
    }
  };


  const handleFileChange = async (e) => {

    const file = e.target.files[0];

    if(!file) {
      return;
    }

    // compress image
    try {
      toast.loading(t('settings.please_wait'));
      const compressedImage = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      })

      const formData = new FormData();
      formData.append("store_image", compressedImage);

      const res = await uploadStoreImage(formData);
      if(res.status == 200) {
        toast.dismiss();
        toast.success(res.data.message);

        // update the image state
        const imagePath = res.data.imageURL;
        await mutate(APIURL);
        location.reload();
      }

    } catch (error) {
      console.error(error);
      toast.dismiss();
      const message = error?.response?.data?.message || t('settings.something_went_wrong');
      toast.error(message)
    }
  }

  const handleFileDelete = async () => {
    try {
      toast.loading(t('settings.please_wait'));

      console.log(uniqueId);

      const res = await deleteStoreImage(uniqueId);
      if(res.status == 200) {
        toast.dismiss();
        toast.success(res.data.message);
        await mutate(APIURL);
        location.reload();
      }

    } catch (error) {
      console.error(error);
      toast.dismiss();
      const message = error?.response?.data?.message || t('settings.something_went_wrong');
      toast.error(message)
    }
  }


  return (
    <Page className="px-8 py-6">
      <h3 className="text-3xl font-light">{t('settings.store_details')}</h3>

      <div className="mt-8 text-sm text-gray-500">
        {/* Store Image Upload Section */}
        <div className="mb-6">
          <label htmlFor="storeImage" className="block mb-1 font-medium">
            {t('settings.store_image')}
          </label>
          <div className="flex flex-col items-start gap-2">
            <input
              type="file"
              name="storeImage"
              id="storeImage"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className='w-24 h-24 rounded-xl flex items-center justify-center relative border border-restro-border-green'>
              {storeImage ? (
                <img 
                  src={getImageURL(storeImage)}
                  alt="Store"
                  className="object-cover w-24 h-24 rounded-xl bg-gray-50"
                />
              ) : (
                <span className="text-gray-400 text-sm">{t('settings.no_image')}</span>
              )}

              {!storeImage ? (
                <label
                  htmlFor="storeImage"
                  className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-restro-gray p-1 rounded-full shadow cursor-pointer hover:bg-restro-button-hover z-10 border border-restro-border-green text-restro-text"
                >
                 <IconUpload size={14} stroke={iconStroke} />
                </label>
              ) : (
                <button
                  className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-restro-gray p-1 rounded-full shadow cursor-pointer hover:bg-restro-red-hover z-10 text-restro-red"
                  onClick={handleFileDelete}
                >
                  <IconTrash size={14} stroke={iconStroke}/>
                </button>
              )}

            </div>
            {/* <p className="text-xs text-gray-400">
              Supported formats: JPG, PNG. Max size: 5MB.
            </p> */}
          </div>
        </div>


        <div>
          <label htmlFor="name" className="block mb-1">
            {t('settings.store_name')}
          </label>
          <input
            ref={storeNameRef}
            type="text"
            name="name"
            id="name"
            defaultValue={storeName}
            placeholder={t('settings.store_name_placeholder')}
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'/>
        </div>
        <div className="mt-4">
          <label htmlFor="panNo" className="block mb-1">
            PAN No.
          </label>
          <input
            ref={panNoRef}
            type="text"
            name="panNo"
            id="panNo"
            defaultValue={panNo}
            placeholder="Store PAN Number"
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'/>
        </div>
        <div className="mt-4">
          <label htmlFor="cbmsPassword" className="block mb-1">
            IRD CBMS Password
          </label>
          <input
            ref={cbmsPasswordRef}
            type="text"
            name="cbmsPassword"
            id="cbmsPassword"
            defaultValue={cbmsPassword}
            placeholder="CBMS Password (optional)"
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'/>
        </div>
        <div className="mt-4">
          <label htmlFor="address" className="block mb-1">
            {t('settings.address')}
          </label>
          <textarea
            ref={addressRef}
            type="text"
            name="address"
            id="address"
            defaultValue={address}
            placeholder={t('settings.address_placeholder')}
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'
          />
        </div>
        <div className="mt-4">
          <label htmlFor="email" className="block mb-1">
            {t('settings.email')}
          </label>
          <input
            ref={emailRef}
            type="email"
            name="email"
            id="email"
            defaultValue={email}
            placeholder={t('settings.email_placeholder')}
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'
          />
        </div>
        <div className="mt-4">
          <label htmlFor="phone" className="block mb-1">
            {t('settings.phone')}
          </label>
          <input
            ref={phoneRef}
            type="tel"
            name="phone"
            id="phone"
            defaultValue={phone}
            placeholder={t('settings.phone_placeholder')}
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'
          />
        </div>
        <div className="mt-4">
          <label htmlFor="currency" className="block mb-1">
            {t('settings.currency')}
          </label>
          <select
            ref={currencyRef}
            name="currency"
            id="currency"
            defaultValue={currency}
            placeholder={t('settings.select_currency')}
            className='block w-full lg:min-w-96 rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover'
          >
            <option value="" hidden>
              {t('settings.select_currency')}
            </option>
            {CURRENCIES.map((item, index) => {
              return (
                <option value={item.cc} key={index}>
                  {item.name} - ({item.symbol})
                </option>
              );
            })}
          </select>
        </div>

        <div className="w-full lg:min-w-96 flex items-center justify-between mt-4">
          <label htmlFor="qrmenu" className="flex items-center gap-2">
            {t('settings.enable_qr_menu')}
            <Popover text={t('settings.qr_menu_tooltip')} />
          </label>

          {/* switch */}
          <label className="relative inline-flex items-center cursor-pointer no-drag">
            <input
              ref={isQRMenuEnabledRef}
              defaultChecked={isQRMenuEnabled}
              type="checkbox"
              name="qrmenu"
              id="qrmenu"
              value=""
              className="sr-only peer"
            />
            <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
          </label>
          {/* switch */}
        </div>

        {
          isQRMenuEnabled && <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={btnDownloadMenuQR}
            className='btn btn-sm transition-colors rounded-xl bg-restro-gray hover:bg-restro-button-hover'
          >
            <IconQrcode stroke={iconStroke} /> {t('settings.download_qr_code')}
          </button>
          <a
            target="_blank"
            href={QR_MENU_LINK}
            className='btn btn-sm transition-colors rounded-xl bg-restro-gray hover:bg-restro-button-hover'
          >
            <IconExternalLink stroke={iconStroke} /> {t('settings.view_digital_menu')}
          </a>
          </div>
        }

         <div className="w-full lg:min-w-96 flex items-center justify-between mt-4">
            <label htmlFor="qrorder" className="flex items-center gap-2">
              {t('settings.enable_qr_order')}
              <Popover text={t('settings.qr_order_tooltip')} />
            </label>

            {/* switch */}
            <label className="relative inline-flex items-center cursor-pointer no-drag">
              <input
                ref={isQROrderEnabledRef}
                defaultChecked={isQROrderEnabled}
                type="checkbox"
                name="qrorder"
                id="qrorder"
                value=""
                className="sr-only peer"
              />
              <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
            </label>
            {/* switch */}
          </div>

          <div className="w-full lg:min-w-96 flex items-center justify-between mt-4">
            <label htmlFor="qrviewbill" className="flex items-center gap-2">
              {t('settings.enable_qr_view_bill', 'Enable View Bill via QR Menu')}
              <Popover text={t('settings.qr_view_bill_tooltip', 'Allow customers scanning Table or Room QR code to view the live/estimate bill of their active order.')} />
            </label>

            {/* switch */}
            <label className="relative inline-flex items-center cursor-pointer no-drag">
              <input
                ref={isQRViewBillEnabledRef}
                defaultChecked={isQRViewBillEnabled}
                type="checkbox"
                name="qrviewbill"
                id="qrviewbill"
                value=""
                className="sr-only peer"
              />
              <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
            </label>
            {/* switch */}
          </div>


        <div className="w-full lg:min-w-96 flex items-center justify-between mt-4">
          <label htmlFor="feedback" className="flex items-center gap-2">
            {t('settings.enable_feedback')}
            <Popover text={t('settings.feedback_tooltip')} />
          </label>

          {/* switch */}
          <label className="relative inline-flex items-center cursor-pointer no-drag">
            <input
              ref={isFeedbackEnabledRef}
              defaultChecked={isFeedbackEnabled}
              type="checkbox"
              name="feedback"
              id="feedback"
              value=""
              className="sr-only peer"
            />
            <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
          </label>
          {/* switch */}
        </div>

        <div className="w-full lg:min-w-96 flex items-center justify-between mt-4">
          <label htmlFor="vatRegistered" className="flex items-center gap-2">
            Is VAT Registered?
            <Popover text="Toggle if the store is VAT registered to print Tax Invoice" />
          </label>

          {/* switch */}
          <label className="relative inline-flex items-center cursor-pointer no-drag">
            <input
              ref={isVatRegisteredRef}
              defaultChecked={isVatRegistered}
              type="checkbox"
              name="vatRegistered"
              id="vatRegistered"
              value=""
              className="sr-only peer"
            />
            <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full  after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:border-restro-bg-gray after:border after:rounded-full after:h-5 after:w-5 after:transition-all bg-restro-checkbox peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-restro-ring-light peer-checked:bg-restro-green peer-checked:after:border-restro-border-green`}></div>
          </label>
          {/* switch */}
        </div>

        {/* Inventory & Out of Stock Selling Policy */}
        <div className="mt-8 pt-8 border-t border-restro-border-green w-full lg:min-w-96">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-xl font-medium text-restro-green-dark">Inventory & Out-of-Stock Selling Policy</h4>
            <Popover text="Choose whether to block sales when inventory ingredients run out, or allow sales to continue into negative stock." />
          </div>
          <p className="text-xs text-gray-500 mb-4">Select how the system behaves when the raw materials/inventory needed for a menu item are depleted.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label
              onClick={() => setStockSellingPolicy('block')}
              className={`p-4 rounded-xl border cursor-pointer transition flex items-start gap-3.5 ${
                stockSellingPolicy === 'block'
                  ? 'border-red-500 bg-red-50/70 dark:bg-red-950/20 shadow-sm ring-1 ring-red-400'
                  : 'border-restro-border-green bg-restro-gray/40 hover:bg-restro-gray'
              }`}
            >
              <input
                type="radio"
                name="stock_selling_policy"
                value="block"
                checked={stockSellingPolicy === 'block'}
                onChange={() => setStockSellingPolicy('block')}
                className="radio radio-error mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Do Not Allow to Sell When Stock Finished</span>
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">Strict Block</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Disallows ordering or adding menu items to cart when required inventory/raw ingredients are finished (0 or insufficient). Protects against selling dishes whose raw materials are out of stock.
                </p>
              </div>
            </label>

            <label
              onClick={() => setStockSellingPolicy('allow_negative')}
              className={`p-4 rounded-xl border cursor-pointer transition flex items-start gap-3.5 ${
                stockSellingPolicy === 'allow_negative'
                  ? 'border-restro-green bg-restro-green-light/20 dark:bg-restro-green-dark/20 shadow-sm ring-1 ring-restro-green'
                  : 'border-restro-border-green bg-restro-gray/40 hover:bg-restro-gray'
              }`}
            >
              <input
                type="radio"
                name="stock_selling_policy"
                value="allow_negative"
                checked={stockSellingPolicy === 'allow_negative'}
                onChange={() => setStockSellingPolicy('allow_negative')}
                className="radio radio-primary mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Support Negative Stock</span>
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">Flexible Deficit</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Allows sales to continue even if stock finishes. Stock balances drop into negative numbers (e.g. -5 kg), and when stock is later refilled or purchased, it automatically calculates and reconciles that negative stock.
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-restro-border-green w-full lg:min-w-96">
          <h4 className="text-xl font-medium mb-4 text-restro-green-dark">Marketplace & Discovery</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            <div className="col-span-1">
              <label className="block mb-1">Business Type</label>
              <select ref={businessTypeRef} defaultValue={businessType || 'restaurant'} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover">
                <option value="restaurant">Restaurant</option>
                <option value="cafe">Cafe</option>
                <option value="lodge">Lodge</option>
                <option value="resort">Resort</option>
                <option value="hotel">Hotel</option>
                <option value="trekking_operator">Trekking Operator</option>
              </select>
            </div>

            <div className="col-span-1">
              <label className="block mb-1">Website URL</label>
              <input ref={websiteRef} type="url" defaultValue={website} placeholder="https://..." className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover"/>
            </div>

            <div className="md:col-span-2">
              <label className="block mb-1">Description</label>
              <textarea ref={descriptionRef} defaultValue={description} placeholder="Short description of your business..." className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green focus:outline-restro-button-hover" rows="3"></textarea>
            </div>

            {/* Location */}
            <div className="col-span-1 md:col-span-2 mt-4 flex justify-between items-center border-b border-restro-border-green pb-1">
              <h5 className="font-medium text-restro-green-dark">Location details</h5>
              <button onClick={handleGetLocation} type="button" className="text-xs bg-restro-green text-white px-3 py-1 rounded-lg flex items-center gap-1 hover:bg-restro-green-button-hover transition-colors">
                <IconMapPin size={14} /> Get Location
              </button>
            </div>
            
            <div className="col-span-1">
              <label className="block mb-1">City</label>
              <input ref={cityRef} type="text" defaultValue={city} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            <div className="col-span-1">
              <label className="block mb-1">State</label>
              <input ref={stateRef} type="text" defaultValue={state} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            <div className="col-span-1">
              <label className="block mb-1">Country</label>
              <input ref={countryRef} type="text" defaultValue={country || 'Nepal'} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            <div className="col-span-1">
              <label className="block mb-1">Postal Code</label>
              <input ref={postalCodeRef} type="text" defaultValue={postalCode} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="block mb-1">Landmark</label>
              <input ref={landmarkRef} type="text" defaultValue={landmark} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            
            <div className="col-span-1">
              <label className="block mb-1">Latitude (GPS)</label>
              <input ref={latitudeRef} type="number" step="any" defaultValue={latitude} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>
            <div className="col-span-1">
              <label className="block mb-1">Longitude (GPS)</label>
              <input ref={longitudeRef} type="number" step="any" defaultValue={longitude} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
            </div>

            {/* Service Toggles */}
            <div className="col-span-1 md:col-span-2 mt-4">
              <h5 className="font-medium mb-2 text-restro-green-dark border-b border-restro-border-green pb-1">Services Offered</h5>
              <div className="flex flex-wrap gap-6 mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input ref={foodOrderingEnabledRef} type="checkbox" defaultChecked={foodOrderingEnabled} className="w-5 h-5 text-restro-green rounded focus:ring-restro-green" />
                  Food Ordering
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input ref={roomBookingEnabledRef} type="checkbox" defaultChecked={roomBookingEnabled} className="w-5 h-5 text-restro-green rounded focus:ring-restro-green" />
                  Room Booking
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input ref={trekkingEnabledRef} type="checkbox" defaultChecked={trekkingEnabled} className="w-5 h-5 text-restro-green rounded focus:ring-restro-green" />
                  Trekking Services
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input ref={deliveryEnabledRef} type="checkbox" defaultChecked={deliveryEnabled} className="w-5 h-5 text-restro-green rounded focus:ring-restro-green" />
                  Delivery
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input ref={pickupEnabledRef} type="checkbox" defaultChecked={pickupEnabled} className="w-5 h-5 text-restro-green rounded focus:ring-restro-green" />
                  Self-Pickup
                </label>
              </div>
            </div>

            {/* Delivery Settings */}
            <div className="col-span-1 md:col-span-2 mt-4">
              <h5 className="font-medium mb-2 text-restro-green-dark border-b border-restro-border-green pb-1">Delivery Settings</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                <div className="col-span-1">
                  <label className="block mb-1">Radius (Km)</label>
                  <input ref={deliveryRadiusKmRef} type="number" defaultValue={deliveryRadiusKm} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
                </div>
                <div className="col-span-1">
                  <label className="block mb-1">Min Order</label>
                  <input ref={minOrderAmountRef} type="number" defaultValue={minOrderAmount} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
                </div>
                <div className="col-span-1">
                  <label className="block mb-1">Delivery Charge</label>
                  <input ref={deliveryChargeRef} type="number" defaultValue={deliveryCharge} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
                </div>
                <div className="col-span-1">
                  <label className="block mb-1">Free Delivery Over</label>
                  <input ref={freeDeliveryThresholdRef} type="number" defaultValue={freeDeliveryThreshold} className="block w-full rounded-lg px-4 py-2 text-restro-text bg-restro-gray border border-restro-border-green" />
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Force Full Sync */}
        {window.isElectron && (
          <div className="mt-8 border border-restro-border-green rounded-2xl p-5 bg-white dark:bg-restro-card-bg shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <IconDatabase className="text-restro-green" size={24} stroke={iconStroke} />
              <h4 className="text-lg font-medium text-restro-text">Force Cloud Synchronization</h4>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              If your offline local database is missing recent changes (like new menu items) from the online website, click the button below to force a complete download of all cloud data.
            </p>
            <button
              type="button"
              className="btn text-white bg-restro-green-light hover:bg-restro-green hover:text-white transition-colors"
              onClick={async () => {
                if(window.confirm('Are you sure you want to download all master data from the cloud? This will overwrite the local offline database with the latest cloud data.')) {
                  toast.loading('Downloading cloud data...', { id: 'full-sync' });
                  try {
                    const res = await window.electron.ipcRenderer.invoke('trigger-full-sync');
                    if(res && res.success) {
                      toast.success('Successfully downloaded all data from cloud!', { id: 'full-sync' });
                    } else {
                      toast.error('Failed to sync: ' + (res?.message || 'Unknown error'), { id: 'full-sync' });
                    }
                  } catch(e) {
                    toast.error('Error: ' + e.message, { id: 'full-sync' });
                  }
                }
              }}
            >
              <IconCloud size={20} />
              Download All Data from Cloud
            </button>

            <div className="mt-8 mb-4 border-t border-gray-200 dark:border-restro-border-green pt-6">
              <h4 className="text-lg font-medium text-restro-text">Force Push Local Changes</h4>
              <p className="text-xs text-gray-500 mb-4">
                Push all locally created sales, purchases, invoices, inventory, and ledgers to the cloud immediately.
              </p>
              <button
                type="button"
                className="btn text-white bg-blue-500 hover:bg-blue-600 hover:text-white transition-colors"
                onClick={async () => {
                  toast.loading('Pushing data to cloud...', { id: 'push-sync' });
                  try {
                    const res = await window.electron.ipcRenderer.invoke('trigger-sync');
                    if(res && res.success !== false) {
                      toast.success('Successfully pushed all local data to cloud!', { id: 'push-sync' });
                    } else if (res && res.message) {
                      toast.error('Sync error: ' + res.message, { id: 'push-sync' });
                    } else {
                      toast.success('Sync cycle completed.', { id: 'push-sync' });
                    }
                  } catch(e) {
                    toast.error('Error: ' + e.message, { id: 'push-sync' });
                  }
                }}
              >
                <IconCloud size={20} />
                Push Local Data to Cloud
              </button>
            </div>

          </div>
        )}

        {/* Workflow & Offline Sync Mode */}
        <div id="workflow-section" className="mt-8 border border-restro-border-green rounded-2xl p-5 bg-white dark:bg-restro-card-bg shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <IconRefresh className="text-restro-green" size={24} stroke={iconStroke} />
            <h4 className="text-lg font-medium text-restro-text">Application Workflow & Offline Sync Mode</h4>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Configure how the desktop POS and KDS behave. Choose whether the app should operate offline with local SQLite database or directly online with the cloud.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              onClick={() => setSyncModeState('offline_sync')}
              className={`cursor-pointer rounded-xl p-4 border transition-colors duration-150 ${syncMode === 'offline_sync' ? 'border-restro-green bg-green-50/50 dark:bg-green-950/20 ring-2 ring-restro-green' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-start gap-3">
                <input 
                  type="radio" 
                  name="syncMode" 
                  checked={syncMode === 'offline_sync'} 
                  onChange={() => setSyncModeState('offline_sync')}
                  className="mt-1 radio radio-sm radio-success"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-restro-text">Offline Work (Sync when online)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Operate offline with local SQLite database. Orders, kitchen display, KOT prints, invoices, and payments work locally. Automatically syncs to cloud MySQL whenever internet connection is restored.
                  </p>
                </div>
              </div>
            </div>

            <div 
              onClick={() => setSyncModeState('online_only')}
              className={`cursor-pointer rounded-xl p-4 border transition-colors duration-150 ${syncMode === 'online_only' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 ring-2 ring-blue-500' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-start gap-3">
                <input 
                  type="radio" 
                  name="syncMode" 
                  checked={syncMode === 'online_only'} 
                  onChange={() => setSyncModeState('online_only')}
                  className="mt-1 radio radio-sm radio-info"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-restro-text">Online Only</span>
                    <span className="text-[10px] bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 px-2 py-0.5 rounded-full font-bold">Recommended</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    App connects and communicates directly with cloud SaaS. An active internet connection is used to ensure real-time consistency across all multi-branch devices.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {window.isElectron && (
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-semibold text-red-600 dark:text-red-400">Clear Local Database & Auto Logout</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    Delete the local SQLite database and sync queue. Use this to reset the local POS state and fetch fresh data from the cloud on the next login. You will be automatically logged out.
                  </p>
                </div>
                <button 
                  onClick={async () => {
                    if (window.confirm("Are you sure you want to delete all local offline data and restart the application? Any unsynced data will be lost forever.")) {
                      try {
                        if (window.electron && window.electron.clearLocalData) {
                          toast.loading("Clearing local data and restarting...");
                          const res = await window.electron.clearLocalData();
                          if (res && !res.success) {
                            toast.dismiss();
                            toast.error("Failed to clear: " + res.error);
                          }
                        } else {
                          toast.error("Clear data command not available.");
                        }
                      } catch (e) {
                        toast.dismiss();
                        toast.error("Failed to clear local data: " + e.message);
                      }
                    }
                  }}
                  className="btn btn-outline btn-error btn-sm rounded-lg flex items-center gap-1"
                >
                  <IconTrash size={16} />
                  Clear & Logout
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tip Management Settings (Gated by Superadmin Module: tip_management) */}
        {(() => {
          const user = getUserDetailsInLocalStorage() || {};
          const modulesStr = (data?.enabled_modules !== undefined && data?.enabled_modules !== null)
            ? data.enabled_modules
            : (user?.enabled_modules || "");
          const hasTipModule = modulesStr ? modulesStr.split(",").map(m => m.trim()).includes("tip_management") : false;

          if (!hasTipModule) return null;

          return (
            <div id="tip-management-section" className="mt-8 border border-restro-border-green rounded-2xl p-5 bg-white dark:bg-restro-card-bg shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <IconCoin className="text-amber-500" size={24} stroke={iconStroke} />
                  <div>
                    <h4 className="text-lg font-medium text-restro-text">Tip & Gratuity Management</h4>
                    <p className="text-xs text-gray-500">
                      Enable voluntary tips in POS, Orders, and Room settle bill dialogues. Tips are tracked separately from sales revenue and posted to Tips Payable liability.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isTipEnabled}
                    onChange={(e) => setIsTipEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-restro-green"></div>
                </label>
              </div>

              {isTipEnabled && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-zinc-800 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                      Default Tip Distribution Mode
                    </label>
                    <select
                      value={tipDefaultType}
                      onChange={(e) => setTipDefaultType(e.target.value)}
                      className="select select-bordered select-sm w-full rounded-xl bg-gray-50 dark:bg-zinc-800 text-xs"
                    >
                      <option value="direct">Direct to Serving Staff (Preselect waiter/cashier)</option>
                      <option value="pool">Shared Department / Shift Pool</option>
                      <option value="pending">Pending Allocation (Distribute later in Tip Management)</option>
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Staff assignment can still be customized or chosen during bill settlement.
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                      Quick Tip Percentages (comma separated)
                    </label>
                    <input
                      type="text"
                      value={tipDefaultPercentages}
                      onChange={(e) => setTipDefaultPercentages(e.target.value)}
                      placeholder="e.g. 5,10,15,20"
                      className="input input-bordered input-sm w-full rounded-xl bg-gray-50 dark:bg-zinc-800 text-xs"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Cashier can click these percentage chips for instant 1-click calculation or type custom amount.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <button 
          onClick={btnSave} 
          className='text-white w-full lg:min-w-96 transition  active:scale-95 rounded-xl px-4 py-2 mt-6 bg-restro-green hover:bg-restro-green-button-hover '
        >
          {t('settings.save')}
        </button>
      </div>
    </Page>
  );
}
